import Box from '@mui/material/Box';
import { useTheme } from '@doxyme/ui-foundation/theme';
import { ButtonType } from '@doxyme/ui-foundation/components/button';
import { BodyM, Heading } from '@doxyme/ui-foundation/typography';
import { type NotificationLevel } from '@doxyme/ui-foundation/components/status';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '../../../modal';
import { Status } from '@doxyme/ui-foundation/components/status';

import { useId, type JSX, type ReactNode } from 'react';

type AlertModalButtonProps = {
  text: string;
  onClick?: () => void | Promise<void>;
  isLoading?: boolean;
  status?: Exclude<NotificationLevel, 'loader' | 'spinner'>;
  type?: ButtonType;
};

export type AlertModalProps = {
  isOpen: boolean;
  title: string;
  description: ReactNode;
  primaryButton: AlertModalButtonProps;
  secondaryButton?: AlertModalButtonProps;
  onClose?: () => void;
  statusIcon?: Exclude<NotificationLevel, 'loader'>;
};

export const AlertModal = ({
  isOpen,
  title,
  statusIcon,
  description,
  primaryButton,
  secondaryButton,
  onClose,
}: AlertModalProps): JSX.Element | null => {
  const theme = useTheme();
  const titleId = useId();

  return (
    <Modal isOpen={isOpen} role='alertdialog' aria-labelledby={titleId}>
      <ModalContent>
        <ModalHeader onClose={onClose} />

        <ModalBody>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 4,
              alignSelf: 'stretch',
            }}
          >
            {statusIcon && <Status statusLevel={statusIcon} size={40} />}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                alignSelf: 'stretch',
              }}
            >
              <Heading
                id={titleId}
                tag='h1'
                variant='titleH4'
                fontWeight='medium'
                color={theme.text.generic.primary}
                css={{ margin: 0 }}
              >
                {title}
              </Heading>
              <BodyM
                color={theme.text.generic.secondary}
                css={{ textAlign: 'center' }}
              >
                {description}
              </BodyM>
            </Box>
          </Box>
        </ModalBody>

        <ModalFooter
          layout='equal-buttons'
          submitButton={{
            children: primaryButton.text,
            onClick: primaryButton.onClick,
            type: primaryButton.type ?? 'primary',
            isLoading: primaryButton.isLoading,
          }}
          secondaryButton={
            secondaryButton
              ? {
                  children: secondaryButton.text,
                  onClick: secondaryButton.onClick,
                  type: secondaryButton.type ?? 'secondary',
                  isLoading: secondaryButton.isLoading,
                }
              : undefined
          }
        />
      </ModalContent>
    </Modal>
  );
};
